import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import MainLayout from '@/components/layout/MainLayout';
import { useDialog } from '@/hooks/useDialog';
import type { ReceiptCurrency, ReceiptReportData, ReceiptReportType } from '@/types/reporte';
import { receiptReportAmount, receiptReportCell, receiptReportColumns, uruguayMonthRange } from '@/utils/receiptReportUtils';

export default function ReceiptBreakdownReport({ type, onTypeChange }: { type: ReceiptReportType; onTypeChange?: (type: ReceiptReportType) => void }) {
  const { showAlert } = useDialog();
  const [draft, setDraft] = useState(uruguayMonthRange);
  const [applied, setApplied] = useState(draft);
  const [report, setReport] = useState<ReceiptReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const columns = receiptReportColumns(type);
  const title = type === 'PROPIETARIO' ? 'Recibos de Propietarios' : 'Recibos de Inquilinos';

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    api.get('/reports/receipts', {
      params: { type: type.toLowerCase(), startDate: applied.start, endDate: applied.end }, signal: controller.signal,
    }).then(response => {
      if (!controller.signal.aborted) setReport((response as unknown as { data: ReceiptReportData }).data);
    }).catch(error => {
      if (!controller.signal.aborted) {
        setReport(null);
        setError(error.response?.data?.message || 'No se pudo cargar el reporte. Volvé a aplicar los filtros para reintentar.');
      }
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [type, applied]);

  const exportReport = async (format: 'print' | 'pdf' | 'excel') => {
    setExporting(true);
    const printWindow = format !== 'excel' ? window.open('about:blank', '_blank') : null;
    try {
      if (format !== 'excel' && !printWindow) throw new Error('Permití las ventanas emergentes para abrir la impresión.');
      const response = await api.get('/reports/receipts', {
        params: { type: type.toLowerCase(), startDate: applied.start, endDate: applied.end, export: format === 'print' ? 'pdf' : format },
      });
      const file = (response as unknown as { data: { file: string } }).data.file;
      const base = new URL(`${api.defaults.baseURL!.replace(/\/$/, '')}/`, window.location.origin);
      const url = new URL(file, base).href;
      if (format !== 'excel') {
        if (!printWindow) throw new Error('Permití las ventanas emergentes para abrir la impresión.');
        printWindow.location.href = format === 'print' ? `${url}#print` : url;
      } else {
        const download = await axios.get(url, { responseType: 'blob', withCredentials: true });
        const blobUrl = URL.createObjectURL(download.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `recibos-${type.toLowerCase()}-${applied.start}-${applied.end}.csv`;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (error) {
      printWindow?.close();
      showAlert({ type: 'error', title: 'Error al exportar', message: error instanceof Error ? error.message : 'No se pudo exportar el reporte.' });
    } finally { setExporting(false); }
  };

  return <MainLayout compact>
    <div className="w-full space-y-5">
      <Link to="/reports" className="text-sm text-blue-700">← Reportes</Link>
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{title}</h1>
        <div className="flex flex-wrap gap-2">
          <button className="bg-blue-700 text-white rounded px-4 py-2 disabled:opacity-50" disabled={loading || exporting || !report} onClick={() => exportReport('print')}>Imprimir</button>
          <button className="bg-red-600 text-white rounded px-4 py-2 disabled:opacity-50" disabled={loading || exporting || !report} onClick={() => exportReport('pdf')}>Exportar PDF</button>
          <button className="bg-green-700 text-white rounded px-4 py-2 disabled:opacity-50" disabled={loading || exporting || !report} onClick={() => exportReport('excel')}>Exportar Excel</button>
        </div>
      </div>
      {onTypeChange ? <fieldset className="flex flex-wrap gap-2">
        <legend className="text-sm font-medium mb-2">Tipo de recibos</legend>
        {(['INQUILINO', 'PROPIETARIO'] as ReceiptReportType[]).map(value => <button
          key={value} type="button" aria-pressed={type === value} disabled={exporting}
          onClick={() => onTypeChange(value)}
          className={`px-4 py-2 rounded border disabled:opacity-50 ${type === value ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-800 border-gray-300'}`}
        >{value === 'INQUILINO' ? 'Inquilinos' : 'Propietarios'}</button>)}
      </fieldset> : null}
      <form className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-4" onSubmit={event => { event.preventDefault(); setApplied({ ...draft }); }}>
        <label className="text-sm">Fecha inicio<input aria-label="Fecha inicio" required type="date" value={draft.start} max={draft.end} onChange={e => setDraft({ ...draft, start: e.target.value })} className="block border rounded px-3 py-2 mt-1" /></label>
        <label className="text-sm">Fecha fin<input aria-label="Fecha fin" required type="date" value={draft.end} min={draft.start} onChange={e => setDraft({ ...draft, end: e.target.value })} className="block border rounded px-3 py-2 mt-1" /></label>
        <button className="bg-blue-700 text-white rounded px-4 py-2 disabled:opacity-50" disabled={loading || exporting}>Aplicar filtros</button>
        <p className="text-xs text-gray-600">Días completos en hora de Uruguay.</p>
      </form>
      {loading ? <p role="status">Cargando reporte…</p> : error ? <p role="alert" className="text-red-700">{error}</p> : report ? <section className="bg-white border rounded-lg p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-lg">{report.title}</h2><p>{report.dateLabel}: {report.period.label}</p>
        </div>
        <p className="text-sm text-gray-600 mb-3">{report.rows.length} recibos activos</p>
        <p className="text-xs text-gray-600 mb-3">Se priorizan los importes guardados. Si falta el desglose de comisión o IVA, se calculan con los porcentajes del contrato, como en el listado de recibos.</p>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-gray-500">{columns.map(column => <th scope="col" key={column.key} className={`font-medium p-2 ${column.numeric ? 'text-right' : 'text-left'} whitespace-nowrap`}>{column.label}</th>)}</tr></thead>
          <tbody>{report.rows.length ? report.rows.map(row => <tr key={row.id}>{columns.map(column => <td key={column.key} title={column.numeric && typeof row.observacion_financiera === 'string' ? row.observacion_financiera : undefined} className={`p-2 ${column.numeric ? 'text-right whitespace-nowrap tabular-nums' : column.key.endsWith('_nombre') ? 'text-left' : 'text-left whitespace-nowrap'}`}>{receiptReportCell(row, column)}</td>)}</tr>) : <tr><td colSpan={columns.length} className="p-6 text-center">No se encontraron recibos.</td></tr>}</tbody>
          <tfoot className="border-t border-gray-500">{(['UYU', 'USD'] as ReceiptCurrency[]).map(currency => <tr key={currency}>{columns.map((column, index) => {
            const total = report.totals[currency][column.key];
            return <td key={column.key} className={`p-2 ${column.numeric ? 'text-right whitespace-nowrap tabular-nums' : 'text-left'}`}>{total ? <>{receiptReportAmount(total.amount)}{total.partial ? <span className="block text-xs text-gray-600">parcial</span> : null}</> : index === 2 ? (currency === 'UYU' ? 'Total Pesos' : 'Total Dólares') : ''}</td>;
          })}</tr>)}</tfoot>
        </table></div>
        {report.rows.some(row => row.observacion_financiera) ? <p className="mt-3 text-xs text-gray-600">«—» indica un importe que no se puede determinar con el desglose registrado. Los totales afectados son parciales.</p> : null}
      </section> : null}
    </div>
  </MainLayout>;
}
